/**
 * Typed client for the SentiqCommand API. Pure (no React/DOM beyond fetch), so it is unit-testable
 * in the node vitest env. The access token is held in memory by the caller and passed per request —
 * we never read it from localStorage (XSS-exfiltration surface); the API also sets httpOnly cookies.
 */

export interface Workspace {
  tenantId: string;
  name: string;
  slug: string;
  role: string;
}
export interface LoginResult {
  totpRequired: boolean;
  workspaces: Workspace[];
  platformAdmin: boolean;
  preauth: string;
}
export interface Session {
  access: string;
  user: { id: string; email: string } | Record<string, unknown>;
  tenant: { id: string; name: string };
  role: string;
}
export interface PlatformSession {
  access: string;
  user: { id: string };
}
export interface NewTenant {
  name: string;
  slug: string;
  owner: { email: string; password: string };
}
export interface CreatedTenant {
  tenant: { id: string; name: string; slug: string };
  owner: { id: string; email: string; created: boolean };
}
export interface Site {
  id: string;
  name: string;
  site_code: string;
  region: string | null;
  criticality: string | null;
  lat: number | null;
  lon: number | null;
  online: boolean;
  active_alarms: number;
  worst: number; // 0 none · 1 info · 2 warning · 3 major · 4 critical
}
export interface ControllerRef {
  id: string;
  gateway_id: string;
  display_name: string | null;
  profile_id: string;
}
export interface Gateway {
  id: string;
  serial: string;
  online: boolean;
  last_seen_at: string | null;
  agent_version: string | null;
  enrolled: boolean;
  cert_expires_at: string | null;
  /** RutOS version as the router reports it. Null = never read, which is not "out of date". */
  firmware: string | null;
  firmware_checked_at: string | null;
  controller_count: number;
  controllers: ControllerRef[];
  /** Commissioning chain (runbook phase 8), each the moment the step last held. */
  token_live: boolean;
  enrol_token_expires_at: string | null;
  enrolled_at: string | null;
  hw_serial: string | null;
  wg_address: string | null;
  edge_mode: 'cloud' | 'site' | string;
  edge_status: 'stopped' | 'running' | 'backoff' | 'unreachable' | string;
  edge_last_error: string | null;
  edge_last_poll_at: string | null;
  hub_peer_synced_at: string | null;
}

export interface PanelPoint {
  key: string;
  label: string;
  unit: string | null;
  value: number | null;
  quality: string | null;
  enumLabel: string | null;
}
export interface ControllerPanel {
  controller: {
    id: string;
    display_name: string | null;
    profile_id: string;
    profile_name: string;
    /** 'genset' or 'ats' — decides which facia is drawn and which rows are shown. */
    device_class: 'genset' | 'ats';
    site_name: string;
    site_code: string;
  };
  online: boolean;
  last_ts: string | null;
  mode: PanelPoint;
  /** Where the site router reports it is. Null when GNSS is absent, disabled, or has no fix. */
  position: { lat: number; lon: number; address: string | null; fix_at: string | null } | null;
  /** Router-mediated actions this site is physically wired for, e.g. controller_power_cycle. */
  siteActions: string[];
  /**
   * What this module reports it can actually do. `blocked` actions have no route at all and are not
   * offered; `resetViaStopSelect` means clearing alarms will select Stop/Reset — it moves the plant,
   * so it cannot be a one-click button.
   */
  control: {
    known: boolean;
    supported: string[];
    unsupported: string[];
    blocked: string[];
    resetViaStopSelect: boolean;
  };
  groups: { title: string; points: PanelPoint[] }[];
  alarms: { id: string; code: string; label: string; severity: string; raised_at: string }[];
}
/** One alarm occurrence — the log is the full history, not just what is currently ringing. */
export interface AlarmRecord {
  id: string;
  controller_id: string | null;
  code: string;
  label: string;
  severity: 'warning' | 'major' | 'critical' | string;
  source: string;
  raised_at: string;
  cleared_at: string | null;
  acked_at: string | null;
  ack_note: string | null;
  site_name: string | null;
  site_code: string | null;
  controller_name: string | null;
}

export interface CommandResult {
  id: string;
  action: string;
  status: string;
  modbus_response: number | null;
  error: string | null;
}
export interface WritableParam {
  key: string;
  label: string;
  address: number;
  page: number;
  offset: number;
  unit: string | null;
  min: number | null;
  max: number | null;
  group: string | null;
  current: number;
}
export interface EngWriteResult {
  address: number;
  value_before: number;
  value_written: number;
  value_readback: number;
  verified: boolean;
  break_glass: boolean;
  label: string;
}
export type EngSessionType = 'register_write' | 'config_suite';
export interface EngSessionSummary {
  id: string;
  type: EngSessionType;
  reason: string;
  status: string;
  opened_at: string;
  closed_at: string | null;
  opened_by: string | null;
  write_count: number;
  mismatch_count: number;
}
export interface ConnectionInfo {
  configSuite: { host: string | null; port: number; ready: boolean };
  wireguard: { ready: boolean; pending: string[]; config: string | null };
}
export interface ConfigArtefact {
  id: string;
  kind: 'pre' | 'post';
  filename: string;
  sha256: string;
  byte_size: number;
  uploaded_at: string;
  uploaded_by: string | null;
}
export interface EngSessionDetail {
  session: {
    id: string;
    type: EngSessionType;
    status: string;
    reason: string;
    opened_at: string;
    closed_at: string | null;
    opened_by: string | null;
  };
  writes: (EngWriteResult & { justification: string | null; ts: string })[];
  artefacts: {
    pre: ConfigArtefact | null;
    post: ConfigArtefact | null;
    changed: boolean | null;
  };
}
export interface CertBundle {
  gateway_id: string;
  fingerprint: string;
  not_after: string;
  bundle: { cert: string; key: string; ca: string };
}

/** What the wizard shows once: the token the router enrols itself with, and the line to run. */
export interface EnrolToken {
  gateway_id: string;
  site_code: string;
  token: string;
  expires_at: string;
  enrol_url: string;
  command: string;
}

export interface NewSite {
  name: string;
  site_code: string;
  region?: string;
  criticality?: 'low' | 'standard' | 'high';
  lat?: number;
  lon?: number;
  address?: string;
  notes?: string;
}
export interface NewController {
  profile_id: string;
  display_name?: string;
  model?: string;
  firmware?: string;
  slave_id?: number;
  host?: string;
  modbus_port?: number;
  /** Victron (victron-mqtt profile) only: the Cerbo's VRM portal id. */
  victron_portal_id?: string;
}
export interface ProfileSummary {
  id: string;
  name: string;
  protocol: string;
}
export type Role = 'viewer' | 'owner_operator' | 'operator' | 'engineer' | 'tenant_admin';

/** A Victron system's generator mode as the owner sees it (brief §6). */
export type GenMode = 'start' | 'stop' | 'auto';
export type GenState = 'running' | 'starting' | 'standby' | 'stopped' | 'failed' | 'unknown';

export interface VictronSystemRef {
  controllerId: string;
  siteId: string;
  siteName: string;
  systemName: string | null;
}

/** GET /victron/systems/:id — already interpreted server-side (api/src/victron/view.ts). */
export interface VictronView {
  controllerId: string;
  siteId: string;
  siteName: string;
  systemName: string;
  online: boolean;
  lastUpdate: string | null;
  capabilities: string[];
  battery: {
    soc: number | null;
    power: number | null;
    voltage: number | null;
    current: number | null;
    timeToGo: number | null;
    description: string | null;
  };
  solar: { power: number | null; acPower: number | null; dcPower: number | null };
  load: { power: number | null; todayAvg: number | null };
  generator: {
    mode: GenMode | null;
    state: GenState;
    stateLabel: string | null;
    power: number | null;
    runReason: string | null;
    runHours: number | null;
    socStart: number | null;
    socStop: number | null;
    socAutostartEnabled: boolean | null;
  };
  settings: { lowSocAlarmPct: number; pvArrayKwp: number | null; pvArrayUpdatedAt: string | null };
  alarms: { code: string; label: string; severity: string; raisedAt: string }[];
  lastCommand: {
    action: string;
    label: string;
    status: string;
    by: string | null;
    at: string;
    error: string | null;
  } | null;
  canControl: boolean;
}
export interface Member {
  user_id: string;
  email: string;
  status: string;
  role: Role;
  is_you: boolean;
}
export interface TenantRow {
  id: string;
  name: string;
  slug: string;
  control_enabled: boolean;
  member_count: number;
  site_count: number;
}

export interface Connectivity {
  online: boolean;
  active: {
    active_sim: string | null;
    operator: string | null;
    rsrp: number | null;
    rsrq: number | null;
    sinr: number | null;
    data_used_mb: number | null;
    failover_count_24h: number | null;
    ts: string;
  } | null;
  standby: {
    active_sim: string | null;
    operator: string | null;
    rsrp: number | null;
    ts: string;
  } | null;
}

export type ChannelType = 'telegram' | 'email' | 'sms' | 'phone';
export interface NotifyChannelRow {
  id: string;
  type: ChannelType;
  name: string | null;
  enabled: boolean;
  config: Record<string, string | number | boolean>;
  secretSet: boolean;
  configured: boolean;
  routing: {
    severity_min: string;
    notify_alarms: boolean;
    notify_run_status: boolean;
    /** The 07:00 Victron site report (telegram/email channels only). */
    notify_daily_report: boolean;
    /** null = every site in the workspace. */
    site_id: string | null;
    site_name: string | null;
  };
}
export interface NewNotifyChannel {
  type: ChannelType;
  name: string;
  config: Record<string, string>;
  severity_min?: string;
  notify_alarms?: boolean;
  notify_run_status?: boolean;
  notify_daily_report?: boolean;
  site_id?: string | null;
}
export interface NotifyChannelPatch {
  name?: string;
  enabled?: boolean;
  config?: Record<string, string>;
  severity_min?: string;
  notify_alarms?: boolean;
  notify_run_status?: boolean;
  notify_daily_report?: boolean;
  /** Omit to leave the scope alone; null widens to the workspace; a uuid narrows to one site. */
  site_id?: string | null;
}

/** One send attempt, as recorded at the time — channel name and type are the stored copies, so a
 *  delivery stays readable after its channel is renamed or deleted. */
export interface NotifyDelivery {
  id: string;
  channel_id: string | null;
  channel_type: ChannelType;
  channel_name: string | null;
  kind: 'alarm' | 'clear' | 'run';
  subject: string;
  status: 'sent' | 'failed';
  error: string | null;
  created_at: string;
}

/** A failed API call — carries the HTTP status so callers can branch (403/404/409/422/503). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export class ApiClient {
  private token: string | null = null;
  /** In-flight token refresh, shared so a burst of 401s triggers only one refresh. */
  private refreshing: Promise<boolean> | null = null;
  /** Called when the session is truly gone (access expired AND refresh failed) → back to login. */
  private onAuthExpired: (() => void) | null = null;

  /** baseUrl defaults to same-origin /api (see the Vite dev proxy / prod reverse proxy). */
  constructor(private readonly baseUrl = '/api') {}

  setToken(token: string | null): void {
    this.token = token;
  }
  setOnAuthExpired(cb: (() => void) | null): void {
    this.onAuthExpired = cb;
  }

  /** Try to mint a fresh access token from the httpOnly refresh cookie. Deduped across callers. */
  private async doRefresh(): Promise<boolean> {
    if (!this.refreshing) {
      this.refreshing = (async () => {
        try {
          const res = await fetch(this.baseUrl + '/auth/refresh', {
            method: 'POST',
            headers: { accept: 'application/json' },
            credentials: 'include',
          });
          if (!res.ok) return false;
          const s = (await res.json()) as { access?: string };
          if (s?.access) {
            this.token = s.access;
            return true;
          }
          return false;
        } catch {
          return false;
        }
      })();
      void this.refreshing.finally(() => {
        this.refreshing = null;
      });
    }
    return this.refreshing;
  }

  private async req<T>(method: string, path: string, body?: unknown, retried = false): Promise<T> {
    const headers: Record<string, string> = { accept: 'application/json' };
    if (body !== undefined) headers['content-type'] = 'application/json';
    if (this.token) headers['authorization'] = `Bearer ${this.token}`;

    const res = await fetch(this.baseUrl + path, {
      method,
      headers,
      credentials: 'include',
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });

    // Access token expired mid-session: silently refresh once and retry, rather than surfacing the
    // 401 to the user. Never on the /auth/* endpoints themselves (avoids a refresh loop).
    if (res.status === 401 && !retried && !path.startsWith('/auth/')) {
      if (await this.doRefresh()) return this.req<T>(method, path, body, true);
      this.token = null;
      this.onAuthExpired?.(); // refresh cookie is gone too → send them to login cleanly
    }

    const text = await res.text();
    const data: unknown = text ? JSON.parse(text) : null;
    if (!res.ok) {
      const msg =
        (data as { error?: string })?.error ??
        `${res.status} ${res.statusText || 'request failed'}`;
      throw new ApiError(res.status, msg);
    }
    return data as T;
  }

  // ---- auth ----
  login(email: string, password: string): Promise<LoginResult> {
    return this.req('POST', '/auth/login', { email, password });
  }
  openSession(preauth: string, tenantId: string, totpCode?: string): Promise<Session> {
    return this.req('POST', '/auth/session', {
      preauth,
      tenantId,
      ...(totpCode ? { totpCode } : {}),
    });
  }
  /** Restore a session from the httpOnly refresh cookie (page reload). */
  refresh(): Promise<Session> {
    return this.req('POST', '/auth/refresh');
  }
  openPlatformSession(preauth: string, totpCode?: string): Promise<PlatformSession> {
    return this.req('POST', '/auth/platform-session', {
      preauth,
      ...(totpCode ? { totpCode } : {}),
    });
  }
  logout(): Promise<{ ok: boolean }> {
    return this.req('POST', '/auth/logout');
  }

  // ---- platform admin ----
  createTenant(t: NewTenant): Promise<CreatedTenant> {
    return this.req('POST', '/admin/tenants', t);
  }

  // ---- alarms ----
  listAlarms(opts: { status?: 'active' | 'all'; site?: string; limit?: number } = {}): Promise<{
    alarms: AlarmRecord[];
  }> {
    const q = new URLSearchParams();
    if (opts.status) q.set('status', opts.status);
    if (opts.site) q.set('site', opts.site);
    if (opts.limit) q.set('limit', String(opts.limit));
    const qs = q.toString();
    return this.req('GET', `/alarms${qs ? `?${qs}` : ''}`);
  }
  getNotifySettings(): Promise<{ run_report_ms: number; min_ms: number; max_ms: number }> {
    return this.req('GET', '/notify/settings');
  }
  setRunReportMs(ms: number): Promise<{ run_report_ms: number }> {
    return this.req('PATCH', '/notify/settings', { run_report_ms: ms });
  }
  telegramLinkCode(): Promise<{ code: string; expires_at: string; ttl_seconds: number }> {
    return this.req('POST', '/telegram/link-code');
  }
  telegramLinks(): Promise<{
    links: {
      id: string;
      chat_id: string;
      telegram_username: string | null;
      linked_at: string;
      email: string;
    }[];
  }> {
    return this.req('GET', '/telegram/links');
  }
  revokeTelegramLink(id: string): Promise<{ revoked: true }> {
    return this.req('DELETE', `/telegram/links/${id}`);
  }
  ackAlarm(id: string, note?: string): Promise<{ ok: true }> {
    return this.req('POST', `/alarms/${id}/ack`, note ? { note } : {});
  }

  // ---- provisioning ----
  listSites(): Promise<{ sites: Site[] }> {
    return this.req('GET', '/sites');
  }
  createSite(s: NewSite): Promise<{ id: string; name: string; site_code: string }> {
    return this.req('POST', '/sites', s);
  }
  listGateways(siteId: string): Promise<{ gateways: Gateway[] }> {
    return this.req('GET', `/sites/${siteId}/gateways`);
  }
  createGateway(siteId: string, serial: string): Promise<{ id: string; serial: string }> {
    return this.req('POST', `/sites/${siteId}/gateways`, { serial });
  }
  createController(
    gatewayId: string,
    c: NewController,
  ): Promise<{ id: string; gateway_id: string; profile_id: string }> {
    return this.req('POST', `/gateways/${gatewayId}/controllers`, c);
  }
  issueCert(gatewayId: string): Promise<CertBundle> {
    return this.req('POST', `/gateways/${gatewayId}/cert`);
  }
  issueEnrolToken(gatewayId: string): Promise<EnrolToken> {
    return this.req('POST', `/gateways/${gatewayId}/enrol-token`, {});
  }
  getControllerPanel(controllerId: string): Promise<ControllerPanel> {
    return this.req('GET', `/controllers/${controllerId}/panel`);
  }
  getConnectivity(controllerId: string): Promise<Connectivity> {
    return this.req('GET', `/controllers/${controllerId}/connectivity`);
  }
  // ---- control (§7: operator+, kill switch, typed confirmation) ----
  issueCommand(
    controllerId: string,
    action: string,
    confirm: string,
  ): Promise<{ commandId: string; status: string }> {
    return this.req('POST', `/controllers/${controllerId}/commands`, { action, confirm });
  }
  getCommand(commandId: string): Promise<CommandResult> {
    return this.req('GET', `/commands/${commandId}`);
  }

  // ---- Victron (Cerbo GX) sites ----
  listVictronSystems(): Promise<{ systems: VictronSystemRef[] }> {
    return this.req('GET', '/victron/systems');
  }
  getVictronSystem(controllerId: string): Promise<VictronView> {
    return this.req('GET', `/victron/systems/${controllerId}`);
  }
  setGeneratorMode(
    controllerId: string,
    mode: GenMode,
  ): Promise<{ commandId: string; status: string }> {
    return this.req('POST', `/victron/systems/${controllerId}/generator`, { mode, confirm: true });
  }
  setGeneratorSoc(
    controllerId: string,
    start: number,
    stop: number,
  ): Promise<{ commandId: string; status: string }> {
    return this.req('PUT', `/victron/systems/${controllerId}/generator-soc`, { start, stop });
  }
  setLowSocAlarm(controllerId: string, lowSocPct: number): Promise<{ ok: boolean }> {
    return this.req('PUT', `/victron/systems/${controllerId}/alarm-settings`, { lowSocPct });
  }
  setSolarArray(
    controllerId: string,
    kwp: number,
    installerDirected: boolean,
  ): Promise<{ ok: boolean }> {
    return this.req('PUT', `/victron/systems/${controllerId}/solar-array`, {
      kwp,
      installerDirected,
    });
  }
  getReportPreview(controllerId: string): Promise<{ text: string }> {
    return this.req('GET', `/victron/systems/${controllerId}/report-preview`);
  }

  // ---- Engineer Mode: Tier A remote configuration (§7A) ----
  listWritable(controllerId: string): Promise<{ params: WritableParam[] }> {
    return this.req('GET', `/controllers/${controllerId}/writable`);
  }
  openEngSession(
    controllerId: string,
    reason: string,
    confirm: string,
    type: EngSessionType = 'register_write',
  ): Promise<{ sessionId: string; expiresAt: string; type: EngSessionType }> {
    return this.req('POST', `/controllers/${controllerId}/eng-sessions`, { reason, confirm, type });
  }
  writeParam(
    sessionId: string,
    address: number,
    value: number,
    justification?: string,
  ): Promise<EngWriteResult> {
    return this.req('POST', `/eng-sessions/${sessionId}/writes`, {
      address,
      value,
      ...(justification ? { justification } : {}),
    });
  }
  closeEngSession(
    sessionId: string,
  ): Promise<{ closed: string; changes: { address: number; verified: boolean }[] }> {
    return this.req('POST', `/eng-sessions/${sessionId}/close`);
  }
  listEngSessions(controllerId: string): Promise<{ sessions: EngSessionSummary[] }> {
    return this.req('GET', `/controllers/${controllerId}/eng-sessions`);
  }
  getEngSession(sessionId: string): Promise<EngSessionDetail> {
    return this.req('GET', `/eng-sessions/${sessionId}`);
  }
  getConnection(controllerId: string): Promise<ConnectionInfo> {
    return this.req('GET', `/controllers/${controllerId}/connection`);
  }
  uploadConfigArtefact(
    sessionId: string,
    kind: 'pre' | 'post',
    filename: string,
    contentBase64: string,
  ): Promise<ConfigArtefact> {
    return this.req('POST', `/eng-sessions/${sessionId}/config-artefact`, {
      kind,
      filename,
      contentBase64,
    });
  }
  getConfigArtefact(id: string): Promise<{
    id: string;
    filename: string;
    sha256: string;
    kind: string;
    contentBase64: string;
  }> {
    return this.req('GET', `/config-artefacts/${id}`);
  }
  updateSite(siteId: string, patch: Partial<NewSite>): Promise<{ id: string }> {
    return this.req('PATCH', `/sites/${siteId}`, patch);
  }
  decommissionSite(siteId: string): Promise<{ decommissioned: string }> {
    return this.req('DELETE', `/sites/${siteId}`);
  }
  decommissionGateway(gatewayId: string): Promise<{ decommissioned: string }> {
    return this.req('DELETE', `/gateways/${gatewayId}`);
  }
  removeController(controllerId: string): Promise<{ removed: string }> {
    return this.req('DELETE', `/controllers/${controllerId}`);
  }

  // ---- catalog ----
  listProfiles(): Promise<{ profiles: ProfileSummary[] }> {
    return this.req('GET', '/profiles');
  }

  // ---- team ----
  listTeam(): Promise<{ members: Member[] }> {
    return this.req('GET', '/team');
  }
  addMember(m: {
    email: string;
    role: Role;
    password?: string;
  }): Promise<{ userId: string; created: boolean }> {
    return this.req('POST', '/team', m);
  }
  setMemberRole(userId: string, role: Role): Promise<{ userId: string; role: Role }> {
    return this.req('PATCH', `/team/${userId}`, { role });
  }
  removeMember(userId: string): Promise<{ removed: string }> {
    return this.req('DELETE', `/team/${userId}`);
  }

  // ---- notifications ----
  listChannels(): Promise<{ channels: NotifyChannelRow[] }> {
    return this.req('GET', '/notify/channels');
  }
  createChannel(c: NewNotifyChannel): Promise<{ id: string }> {
    return this.req('POST', '/notify/channels', c);
  }
  updateChannel(id: string, patch: NotifyChannelPatch): Promise<{ id: string }> {
    return this.req('PATCH', `/notify/channels/${id}`, patch);
  }
  deleteChannel(id: string): Promise<{ removed: string }> {
    return this.req('DELETE', `/notify/channels/${id}`);
  }
  listDeliveries(opts: { limit?: number; status?: 'sent' | 'failed' } = {}): Promise<{
    deliveries: NotifyDelivery[];
  }> {
    const q = new URLSearchParams();
    if (opts.limit) q.set('limit', String(opts.limit));
    if (opts.status) q.set('status', opts.status);
    const qs = q.toString();
    return this.req('GET', `/notify/deliveries${qs ? `?${qs}` : ''}`);
  }
  testChannel(id: string): Promise<{ ok: boolean }> {
    return this.req('POST', `/notify/channels/${id}/test`);
  }

  // ---- platform ----
  listTenants(): Promise<{ tenants: TenantRow[] }> {
    return this.req('GET', '/admin/tenants');
  }
  updateTenant(
    id: string,
    patch: { name?: string; controlEnabled?: boolean },
  ): Promise<{ id: string }> {
    return this.req('PATCH', `/admin/tenants/${id}`, patch);
  }
}
